import React, { memo, useEffect, useState } from "react";
import { cookiesKeyNames } from "@revbox/common-fe";
import { Card, Placeholder, Nav } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import { impressionsAndClicksLabels, localeSwitch, seriesNames, buttonLabel } from "@revbox/localization";
import { getPeriodCampaignImpressionsAndClicks } from "../../../services/micro";
import { useCookiesContext } from "../../../contexts/CookiesContext";
import TrackableButton from "../../../mixpanel/components/TrackableButton";
import { startOfDay, endOfDay, addDays } from "date-fns";
import DatePicker from "../../../pub/components/DatePicker";
import { decrypt } from "../../../helpers/crypto";

function getCSSVariableValue(publisherId, variableName) {
  if (publisherId == 34 || publisherId == 73) {
    if (variableName == '--chart-color-1') {
      return '#c4e3cc';
    } else if (variableName == '--chart-color-2') {
      return '#76CC8E';
    } else if (variableName == '--chart-color-3') {
      return '#3AB65D';
    }
  }

  if (publisherId == 67 || publisherId == 10) {
    if (variableName == '--chart-color-1') {
      return '#603eea41';
    } else if (variableName == '--chart-color-2') {
      return '#613EEA';
    } else if (variableName == '--chart-color-3') {
      return '#0cb785';
    }
  }

  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
}

const Ranges = [
  {
    label: "today",
    value: [startOfDay(new Date()), endOfDay(new Date())],
  },
  {
    label: "yesterday",
    value: [
      startOfDay(addDays(new Date(), -1)),
      endOfDay(addDays(new Date(), -1)),
    ],
  },
  {
    label: "Last 30 Days",
    value: [startOfDay(addDays(new Date(), -29)), endOfDay(new Date())],
  },
];

function CampaignImpressionsAndClicks(props) {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");
  const publisherId = urlParams.get("pub");

  const { currentPeriod, campaignId, handleOpenDateRangePicker, isDateRangePickerOpen, setIsDateRangePickerOpen, handleDateRange, isIframe } = props;
  const [response, setResponse] = useState([]);
  const [colors, setColors] = useState({
    primary: "",
    chartSecondary: "",
    columnChart: "",
  });

  const { getLanguageAbbr, updateCookiesProperty } = useCookiesContext();
  const language = getLanguageAbbr();
  const { impressionsAndClicks } = impressionsAndClicksLabels[language];
  const formatDate = (date) =>{
    if (!date) {
      return "";
    }
    return `${date.toLocaleString(localeSwitch(language), { month: "short" })} ${date.getDate()} ${date.getFullYear()}`;
  }
  
  useEffect(() => {
    const primary = getCSSVariableValue(publisherId, '--chart-color-3');
    const chartSecondary = getCSSVariableValue(publisherId, '--chart-color-2');
    const columnChart = getCSSVariableValue(publisherId, '--chart-color-1');
    
    if (isIframe) {
      const advId = urlParams.get('advId')
      if (!advId) {
        return
      }
      const decryptedAdvId = decrypt(advId)
      updateCookiesProperty(cookiesKeyNames.skinMode, "light", decryptedAdvId)
      const element = document.querySelector('[data-skin]');
      // idk why it should be like this, but it solves the problem
      if (element && element.hasAttribute('data-sidebar')) {
        element.setAttribute('data-skin', 'light')
        element.setAttribute('data-sidebar', 'light')
      }  
    }

    setColors({ primary, chartSecondary, columnChart });
  }, []);

  useEffect(() => {
    if (currentPeriod[0]) {
      getPeriodCampaignImpressionsAndClicks(
        currentPeriod,
        campaignId,
        token
      ).then((response) => {
        setResponse(response);
      });
    }
  }, [currentPeriod, campaignId, token]); 

  const optionFour = {
    chart: {
      parentHeightOffset: 0,
      stacked: false,
      toolbar: { show: false },
      animations: {
        enabled: true,
      },
      type: "line", 
    },
    grid: {
      borderColor: "rgba(72,94,144, 0.07)",
      padding: {
        top: -20,
        bottom: -20,
        left: 20,
        right: 0,
      },
    },
    colors: [colors.columnChart, colors.chartSecondary, colors.primary], 
    plotOptions: {
      bar: { columnWidth: "50%" },
    },
    stroke: {
      curve: ["smooth", "smooth", "smooth"], 
      width: [1.5, 1.5, 2], 
    },
    fill: {
      type: ["solid", "gradient", "solid"],
      opacity: 1,
      gradient: {
        type: "vertical",
        shadeIntensity: 1,
        opacityFrom: 0.35,
        opacityTo: 0.2,
        stops: [0, 100],
        gradientToColors: ["#f3f5fc"], 
      },
    },
    xaxis: {
      tickAmount: 15,
      labels: {
        style: {
          colors: "#ccc",
          fontSize: "11px",
        },
        rotate: -45,
        rotateAlways: true, 
      },
    },
    yaxis: [
      {
        seriesName: seriesNames[language].clicks,
        opposite: true,
        labels: {
          show: false, 
        },
      },
      {
        seriesName: seriesNames[language].impressions,
        opposite: false,
        labels: {
          show: false, 
        },
      },
      {
        seriesName: seriesNames[language].spend,
        opposite: false,
        labels: {
          show: false, 
        },
      },
    ],
    dataLabels: { enabled: false },
    tooltip: {
      shared: true,
      intersect: false
    },
    legend: { show: true },
  };

  if (response && response.stats) {
    const categories = response.dates;
    optionFour.xaxis.categories = categories;
    optionFour.xaxis.tickAmount = 15;

    return (
      <React.Fragment>
        <Card.Header>
          <div>
            <Card.Title as="h6" className="d-flex align-items-center">
              <div className="me-2">{impressionsAndClicks.title}</div>
              <div className="fs-xs text-secondary">
              ({formatDate(new Date(currentPeriod[0]))} - {formatDate(new Date(currentPeriod[1]))})
              </div>
            </Card.Title>
            <div className="d-flex align-items-center fs-xs text-secondary mb-0 mt-1">
              <i className="ri-information-line me-1"></i>
              {impressionsAndClicks.description}
            </div>
          </div>
          <Nav className="nav-icon nav-icon-sm ms-auto"></Nav>

          
          {isIframe ? (
            <></>
          ) : (
            <div className="d-flex gap-2 mt-3 mt-md-0">
            <TrackableButton
              variant="btn-outline-primary"
              className={`d-flex align-items-center gap-2 btn btn-outline-primary`}
              onClick={handleOpenDateRangePicker}
              campaignId={campaignId}
              buttonName={"Date Range in adv analytics"}
            >
              <span className="fw-semibold">{buttonLabel[language].dateRange}</span>
              <i className="ri-arrow-right-s-line"></i>
              {currentPeriod && currentPeriod.length === 2
                ? ` ${formatDate(currentPeriod[0])} - ${formatDate(
                    currentPeriod[1]
                  )}`
                : ""}
            </TrackableButton>
            {isDateRangePickerOpen && (
              <DatePicker
                isDateRangePickerOpen={isDateRangePickerOpen}
                setIsDateRangePickerOpen={setIsDateRangePickerOpen}
                ranges={Ranges}
                onChange={handleDateRange}
                currentPeriod={currentPeriod}
              />
            )}
          </div>
          )}
        </Card.Header>
        <Card.Body>
          <ReactApexChart
            series={response.stats}
            options={optionFour}
            type="bar"
            height={300}
            className="apex-chart-nine"
          />
        </Card.Body>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <Placeholder
          as={Card.Title}
          animation="glow"
          style={{ color: "var(--bs-body-color)" }}
        >
          <Placeholder style={{ height: 400, width: "100%" }} />
        </Placeholder>
      </React.Fragment>
    );
  }
}

export default memo(CampaignImpressionsAndClicks);